/**
 * Pure geometry for the overlay's native resize/rotate transformer (replaces Konva.Transformer).
 * All coordinates are in book/print px (the model's native space); the controller scales to screen
 * by `stage.scale`. Rotation is degrees, applied about the box center with the standard matrix
 * (y-down ⇒ positive degrees read as clockwise on screen), matching the box's render transform.
 */

import type { Affine } from './text-affine';

export interface Point {
  x: number;
  y: number;
}

export interface TransformRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type ResizeAnchor =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'middle-left'
  | 'middle-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export type HandleId = ResizeAnchor | 'rotate';

export const RESIZE_ANCHORS: readonly ResizeAnchor[] = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

export const ROTATION_SNAPS: readonly number[] = [0, 45, 90, 135, 180, 225, 270, 315];

/**
 * Per-surface snap tuning. Every field is optional; an absent profile (or field) keeps the
 * behavior every editor ships with today, so hosts opt in delta by delta.
 */
export interface SnapProfile {
  /** Move/resize pull distance in screen px (zoom-invariant). Default 2. */
  moveSnapThresholdPx?: number;
  /** Single-box rotation snap angle set. Default ROTATION_SNAPS. */
  rotationSnaps?: readonly number[];
  /** Snap rotation to a stop within this many degrees WITHOUT Shift (Shift still forces the snap). Default off. */
  alwaysOnRotationToleranceDeg?: number;
  /** Group-rotate snap step in degrees (Shift-gated). Default 45. */
  groupRotateSnapStep?: number;
  /** Add the trim (cut) line edges from the zone geometry as move/resize snap stops. Default off. */
  cutLineStops?: boolean;
  /** Add the safe-zone line (trim + safe inset, outer page edges) as move/resize snap stops at page-edge radii. Default off. */
  safeLineStops?: boolean;
  /** Add page-fraction guides per axis (e.g. [0.25, 0.75]) as move/resize snap stops. Default none. */
  pageFractionStops?: readonly number[];
  /** Resize snap also matches sibling box widths/heights (size-match). Default off. */
  sizeMatch?: boolean;
}

const MIN_SIZE = 10;
const ROTATE_HANDLE_DIST = 30;

// Sign of each anchor relative to the box center: -1/0/+1 per axis (0 = that dimension is fixed).
export const ANCHOR_SIGN: Record<ResizeAnchor, Point> = {
  'top-left': { x: -1, y: -1 },
  'top-center': { x: 0, y: -1 },
  'top-right': { x: 1, y: -1 },
  'middle-left': { x: -1, y: 0 },
  'middle-right': { x: 1, y: 0 },
  'bottom-left': { x: -1, y: 1 },
  'bottom-center': { x: 0, y: 1 },
  'bottom-right': { x: 1, y: 1 },
};

/**
 * Shift on a resize handle: both dimensions keep the start proportions. A corner takes the larger of the two
 * axis factors (whichever axis the pointer pushed further, relatively); a SIDE handle has only one live axis,
 * so that axis drives the other — the perpendicular dimension follows instead of standing still. The factor is
 * floored so the driven dimension can never fall under `minSize` while the dragged one still sits on it.
 * A degenerate start dimension has no ratio to preserve and would yield NaN — the sizes pass through.
 */
function lockAspect(sign: Point, w0: number, h0: number, newW: number, newH: number, minSize: number): { w: number; h: number } {
  if (w0 <= 0 || h0 <= 0) return { w: newW, h: newH };
  const driven = sign.x !== 0 && sign.y !== 0 ? Math.max(newW / w0, newH / h0) : sign.x !== 0 ? newW / w0 : newH / h0;
  const scale = Math.max(driven, minSize / w0, minSize / h0);
  return { w: w0 * scale, h: h0 * scale };
}

function rad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function rot(p: Point, deg: number): Point {
  const a = rad(deg);
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: p.x * c - p.y * s, y: p.x * s + p.y * c };
}

function add(a: Point, b: Point): Point {
  return { x: a.x + b.x, y: a.y + b.y };
}

function center(r: TransformRect): Point {
  return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
}

/** World position of each resize anchor for a (possibly rotated) box. */
export function anchorPositions(r: TransformRect, rotationDeg: number): Record<ResizeAnchor, Point> {
  const c = center(r);
  const out = {} as Record<ResizeAnchor, Point>;
  for (const anchor of RESIZE_ANCHORS) {
    const sign = ANCHOR_SIGN[anchor];
    out[anchor] = add(c, rot({ x: (sign.x * r.width) / 2, y: (sign.y * r.height) / 2 }, rotationDeg));
  }

  return out;
}

/** World position of the rotate handle, `distance` book-px above the top edge, rotated with the box. */
export function rotateHandlePosition(r: TransformRect, rotationDeg: number, distance = ROTATE_HANDLE_DIST): Point {
  return add(center(r), rot({ x: 0, y: -r.height / 2 - distance }, rotationDeg));
}

/** Which handle (if any) the pointer is over, within `radius` book-px. Rotate handle wins ties. */
export function hitTestHandle(
  r: TransformRect,
  rotationDeg: number,
  pointer: Point,
  radius: number,
  rotateDistance = ROTATE_HANDLE_DIST,
): HandleId | undefined {
  const within = (p: Point): boolean => Math.hypot(pointer.x - p.x, pointer.y - p.y) <= radius;
  if (within(rotateHandlePosition(r, rotationDeg, rotateDistance))) return 'rotate';
  const positions = anchorPositions(r, rotationDeg);
  for (const anchor of RESIZE_ANCHORS) {
    if (within(positions[anchor])) return anchor;
  }

  return undefined;
}

export interface ResizeInput {
  anchor: ResizeAnchor;
  startRect: TransformRect;
  rotationDeg: number;
  pointer: Point;
  aspectLock?: boolean;
  /** Alt/Option: resize symmetrically about the box centre — opposite sides move together, the centre stays put. */
  fromCenter?: boolean;
  minSize?: number;
}

/**
 * New rect for dragging `anchor` to `pointer`, holding the opposite anchor fixed in world space.
 * Rotation is unchanged. Clamps to `minSize` (no flip), matching the Konva boundBoxFunc.
 */
export function resizeRect(input: ResizeInput): TransformRect {
  const { anchor, startRect, rotationDeg, pointer } = input;
  const minSize = input.minSize ?? MIN_SIZE;
  const sign = ANCHOR_SIGN[anchor];
  const w0 = startRect.width;
  const h0 = startRect.height;
  const c0 = center(startRect);

  if (input.fromCenter) {
    const rel = rot({ x: pointer.x - c0.x, y: pointer.y - c0.y }, -rotationDeg);
    let newW = sign.x !== 0 ? Math.max(minSize, Math.abs(rel.x) * 2) : w0;
    let newH = sign.y !== 0 ? Math.max(minSize, Math.abs(rel.y) * 2) : h0;
    if (input.aspectLock) ({ w: newW, h: newH } = lockAspect(sign, w0, h0, newW, newH, minSize));

    return { x: c0.x - newW / 2, y: c0.y - newH / 2, width: newW, height: newH };
  }

  const worldPivot = add(c0, rot({ x: (-sign.x * w0) / 2, y: (-sign.y * h0) / 2 }, rotationDeg));
  const relLocal = rot({ x: pointer.x - worldPivot.x, y: pointer.y - worldPivot.y }, -rotationDeg);

  let newW = sign.x !== 0 ? Math.max(minSize, Math.abs(relLocal.x)) : w0;
  let newH = sign.y !== 0 ? Math.max(minSize, Math.abs(relLocal.y)) : h0;
  if (input.aspectLock) ({ w: newW, h: newH } = lockAspect(sign, w0, h0, newW, newH, minSize));

  // The pivot is the opposite anchor, so a side handle's driven dimension grows symmetrically about the
  // dragged edge's own midline — the free axis has no reason to prefer one side.
  const newCenter = add(worldPivot, rot({ x: (sign.x * newW) / 2, y: (sign.y * newH) / 2 }, rotationDeg));
  return { x: newCenter.x - newW / 2, y: newCenter.y - newH / 2, width: newW, height: newH };
}

/** Границы кегля для пропорционального углового ресайза текста (book px). Зеркалят строку `px` из
 *  FONT_SIZE_RANGE (spread-editor/shell text-params.model.ts) — движку нельзя импортировать UI-либу,
 *  при изменении диапазона там править и здесь. */
export const TEXT_SCALE_FONT_MIN_PX = 16;
export const TEXT_SCALE_FONT_MAX_PX = 800;

/**
 * Uniform scale of `startRect` by `k`, holding the anchor OPPOSITE the dragged `anchor` (or the box
 * centre with `fromCenter`) fixed in world space — the rect a clamped proportional text resize stops at
 * (FKS-11514: рамка упирается в границу диапазона кегля целиком, пропорция не ломается).
 */
export function scaleRectAroundAnchor(
  startRect: TransformRect,
  rotationDeg: number,
  anchor: ResizeAnchor,
  k: number,
  fromCenter = false,
): TransformRect {
  const w = startRect.width * k;
  const h = startRect.height * k;
  const c0 = center(startRect);
  if (fromCenter) return { x: c0.x - w / 2, y: c0.y - h / 2, width: w, height: h };
  const sign = ANCHOR_SIGN[anchor];
  const pivot = add(c0, rot({ x: (-sign.x * startRect.width) / 2, y: (-sign.y * startRect.height) / 2 }, rotationDeg));
  const newCenter = add(pivot, rot({ x: (sign.x * w) / 2, y: (sign.y * h) / 2 }, rotationDeg));
  return { x: newCenter.x - w / 2, y: newCenter.y - h / 2, width: w, height: h };
}

export interface MatrixResizeInput {
  anchor: ResizeAnchor;
  startRect: TransformRect;
  /** Book-space draw affine of the box — its matrix part, without the interactive rotation. */
  affine: Affine;
  /** Interactive rotation (deg) composed over the affine about the drawn quad's centre. */
  rotationDeg: number;
  pointer: Point;
  aspectLock?: boolean;
  fromCenter?: boolean;
  minSize?: number;
}

// Local offset of a resize anchor from the rect origin: -1 → 0, 0 → mid, +1 → full extent.
function anchorOffset(sign: Point, w: number, h: number): Point {
  return { x: ((sign.x + 1) / 2) * w, y: ((sign.y + 1) / 2) * h };
}

/**
 * resizeRect for a box drawn through an affine (matrix text). The renderer's model is
 * rotation-about-visual-centre ∘ origin-anchored matrix, so the world of a local offset q in a
 * rect at origin o is W(q) = R·L·q + (I−R)·L·m + o + d (m = half-extents, d = the affine's
 * anchoring residue). The pointer maps into the box's local frame through (R·L)⁻¹ and the origin
 * re-solves so the anchor opposite the grab (or the visual centre with fromCenter) stays fixed in
 * world space — the closed form o₁ = fixed − R·L·q₁ − (I−R)·L·m₁ − d.
 */
export function resizeRectWithMatrix(input: MatrixResizeInput): TransformRect {
  const { anchor, startRect, affine, rotationDeg, pointer } = input;
  const minSize = input.minSize ?? MIN_SIZE;
  const sign = ANCHOR_SIGN[anchor];
  const w0 = startRect.width;
  const h0 = startRect.height;

  const [la, lb, lc, ld, le, lf] = affine;
  const a = rad(rotationDeg);
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const rlA = cos * la - sin * lb;
  const rlB = sin * la + cos * lb;
  const rlC = cos * lc - sin * ld;
  const rlD = sin * lc + cos * ld;
  const det = rlA * rlD - rlB * rlC;
  if (Math.abs(det) < 1e-9) return { ...startRect };

  const dx = le - (startRect.x - (la * startRect.x + lc * startRect.y));
  const dy = lf - (startRect.y - (lb * startRect.x + ld * startRect.y));

  const lin = (q: Point): Point => ({ x: la * q.x + lc * q.y, y: lb * q.x + ld * q.y });
  const spin = (p: Point): Point => ({ x: cos * p.x - sin * p.y, y: sin * p.x + cos * p.y });
  const world = (o: Point, m: Point, q: Point): Point => {
    const rq = spin(lin(q));
    const lm = lin(m);
    const rm = spin(lm);
    return { x: rq.x + lm.x - rm.x + o.x + dx, y: rq.y + lm.y - rm.y + o.y + dy };
  };

  const m0 = { x: w0 / 2, y: h0 / 2 };
  const opposite = { x: -sign.x, y: -sign.y };
  const fixed = world({ x: startRect.x, y: startRect.y }, m0, input.fromCenter ? m0 : anchorOffset(opposite, w0, h0));

  const px = pointer.x - fixed.x;
  const py = pointer.y - fixed.y;
  const relX = (rlD * px - rlC * py) / det;
  const relY = (rlA * py - rlB * px) / det;

  const factor = input.fromCenter ? 2 : 1;
  let newW = sign.x !== 0 ? Math.max(minSize, Math.abs(relX) * factor) : w0;
  let newH = sign.y !== 0 ? Math.max(minSize, Math.abs(relY) * factor) : h0;
  if (input.aspectLock) ({ w: newW, h: newH } = lockAspect(sign, w0, h0, newW, newH, minSize));

  const m1 = { x: newW / 2, y: newH / 2 };
  const q1 = input.fromCenter ? m1 : anchorOffset(opposite, newW, newH);
  const rq1 = spin(lin(q1));
  const lm1 = lin(m1);
  const rm1 = spin(lm1);
  return {
    x: fixed.x - rq1.x - (lm1.x - rm1.x) - dx,
    y: fixed.y - rq1.y - (lm1.y - rm1.y) - dy,
    width: newW,
    height: newH,
  };
}

export interface RotateInput {
  center: Point;
  startPointer: Point;
  pointer: Point;
  startRotationDeg: number;
  snap?: boolean;
  snaps?: readonly number[];
  /** Snap without the explicit `snap` flag when the angle sits within this many degrees of a stop. */
  snapToleranceDeg?: number;
}

/** New rotation (degrees, [0,360)) from the angular delta between start and current pointer. */
export function rotateAngle(input: RotateInput): number {
  const a0 = Math.atan2(input.startPointer.y - input.center.y, input.startPointer.x - input.center.x);
  const a1 = Math.atan2(input.pointer.y - input.center.y, input.pointer.x - input.center.x);
  let deg = input.startRotationDeg + ((a1 - a0) * 180) / Math.PI;
  deg = ((deg % 360) + 360) % 360;
  if (input.snap || input.snapToleranceDeg !== undefined) {
    const snaps = input.snaps ?? ROTATION_SNAPS;
    let best = snaps[0];
    let bestD = Infinity;
    for (const s of snaps) {
      const d = Math.min(Math.abs(deg - s), 360 - Math.abs(deg - s));
      if (d < bestD) {
        bestD = d;
        best = s;
      }
    }

    if (input.snap || bestD <= (input.snapToleranceDeg ?? 0)) return best;
  }

  return deg;
}

// --- Marquee selection + multi-select group transform (milestone 2) ---

export interface RectRotation {
  rect: TransformRect;
  rotation: number;
}

export interface GroupMember extends RectRotation {
  id: string;
  /** The member's VISUAL centre (matrix image of the rect centre) in book px — the point a group transform orbits,
   *  so quarter-turned text (rotation in its draw-transform, not rect.rotation) pivots on the frame. Omit → rect centre. */
  pivot?: Point;
}

export interface GroupMemberUpdate {
  id: string;
  rect: TransformRect;
  rotation: number;
}

const GROUP_ROTATE_SNAP_STEP = 45;
const GROUP_MIN_SCALE = 0.02;

function rotateAround(p: Point, c: Point, deg: number): Point {
  return add(c, rot({ x: p.x - c.x, y: p.y - c.y }, deg));
}

/** Axis-aligned bounds of `r` rotated `rotationDeg` about its own center (the four rotated corners). */
export function rotatedAABB(r: TransformRect, rotationDeg: number): TransformRect {
  const c = center(r);
  const corners: Point[] = [
    { x: r.x, y: r.y },
    { x: r.x + r.width, y: r.y },
    { x: r.x + r.width, y: r.y + r.height },
    { x: r.x, y: r.y + r.height },
  ].map(p => rotateAround(p, c, rotationDeg));
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of corners) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Union of the members' rotated AABBs (the group selection box). `undefined` when empty. */
export function groupBounds(members: readonly RectRotation[]): TransformRect | undefined {
  if (members.length === 0) return undefined;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const m of members) {
    const b = rotatedAABB(m.rect, m.rotation);
    if (b.x < minX) minX = b.x;
    if (b.y < minY) minY = b.y;
    if (b.x + b.width > maxX) maxX = b.x + b.width;
    if (b.y + b.height > maxY) maxY = b.y + b.height;
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Normalizes two marquee corners into a positive-size rect. */
export function marqueeRect(a: Point, b: Point): TransformRect {
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), width: Math.abs(a.x - b.x), height: Math.abs(a.y - b.y) };
}

/** True when two axis-aligned rects overlap (shared edges alone do not count). */
export function rectsIntersect(a: TransformRect, b: TransformRect): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

/** Ids of members whose rotated AABB intersects the marquee rect. */
export function marqueeHits(marquee: TransformRect, members: readonly GroupMember[]): string[] {
  return members.filter(m => rectsIntersect(marquee, rotatedAABB(m.rect, m.rotation))).map(m => m.id);
}

export interface GroupResizeInput {
  anchor: ResizeAnchor;
  startGroup: TransformRect;
  members: readonly GroupMember[];
  pointer: Point;
  aspectLock?: boolean;
  /** Alt/Option: scale symmetrically about the group centre — opposite sides move together, the centre stays put. */
  fromCenter?: boolean;
  minScale?: number;
}

// Scale factors + pivot for a group resize. A zero-size group falls back to minScale (no divide-by-zero NaN).
function groupScaleFactors(input: GroupResizeInput): {
  sx: number;
  sy: number;
  pivotX: number;
  pivotY: number;
  centeredX: boolean;
  centeredY: boolean;
} {
  const { anchor, startGroup, pointer } = input;
  const sign = ANCHOR_SIGN[anchor];
  const minScale = input.minScale ?? GROUP_MIN_SCALE;
  const cx = startGroup.x + startGroup.width / 2;
  const cy = startGroup.y + startGroup.height / 2;
  // A side handle under Shift drives the axis it cannot move; that axis has no dragged edge to hang on, so it
  // grows about the group centre — the same symmetry a single box gets from its opposite-anchor pivot.
  const drivenAxis = input.aspectLock && (sign.x === 0) !== (sign.y === 0);
  // An axis pivoting on the centre grows to BOTH sides, so the frame straddles that pivot instead of hanging
  // off it — the members do exactly that, and the live frame has to report the same box.
  const centeredX = !!input.fromCenter || (!!drivenAxis && sign.x === 0);
  const centeredY = !!input.fromCenter || (!!drivenAxis && sign.y === 0);
  const pivotX = centeredX ? cx : sign.x >= 0 ? startGroup.x : startGroup.x + startGroup.width;
  const pivotY = centeredY ? cy : sign.y >= 0 ? startGroup.y : startGroup.y + startGroup.height;
  const rawW =
    sign.x === 0
      ? startGroup.width
      : input.fromCenter
        ? Math.abs(pointer.x - cx) * 2
        : sign.x === 1
          ? pointer.x - pivotX
          : pivotX - pointer.x;
  const rawH =
    sign.y === 0
      ? startGroup.height
      : input.fromCenter
        ? Math.abs(pointer.y - cy) * 2
        : sign.y === 1
          ? pointer.y - pivotY
          : pivotY - pointer.y;
  let sx = sign.x !== 0 ? (startGroup.width > 0 ? Math.max(minScale, rawW / startGroup.width) : minScale) : 1;
  let sy = sign.y !== 0 ? (startGroup.height > 0 ? Math.max(minScale, rawH / startGroup.height) : minScale) : 1;
  if (input.aspectLock) {
    const s = sign.x !== 0 && sign.y !== 0 ? Math.max(sx, sy) : sign.x !== 0 ? sx : sy;
    sx = s;
    sy = s;
  }

  return { sx, sy, pivotX, pivotY, centeredX, centeredY };
}

/**
 * Scales the group's members about the dragged anchor's opposite corner (the group pivot). Each
 * member's center is scaled by (sx,sy) about the pivot and its size by (sx,sy); rotation is preserved.
 * Exact for axis-aligned members; rotated members scale in the group frame (their AABB tracks the box).
 */
export function groupResize(input: GroupResizeInput): GroupMemberUpdate[] {
  const { sx, sy, pivotX, pivotY } = groupScaleFactors(input);
  return input.members.map(m => {
    // Scale the VISUAL centre about the group pivot and translate the rect by that delta (so quarter-turned text
    // tracks the frame); size scales by (sx,sy). For a plain rect the visual centre is the rect centre → unchanged.
    const p = m.pivot ?? center(m.rect);
    const nvx = pivotX + (p.x - pivotX) * sx;
    const nvy = pivotY + (p.y - pivotY) * sy;
    const nw = m.rect.width * sx;
    const nh = m.rect.height * sy;
    const c = center(m.rect);
    return {
      id: m.id,
      rect: { x: c.x + (nvx - p.x) - nw / 2, y: c.y + (nvy - p.y) - nh / 2, width: nw, height: nh },
      rotation: m.rotation,
    };
  });
}

/** The group bounding box after a resize (axis-aligned), for the live frame/handles. */
export function groupResizeBounds(input: GroupResizeInput): TransformRect {
  const sign = ANCHOR_SIGN[input.anchor];
  const { sx, sy, pivotX, pivotY, centeredX, centeredY } = groupScaleFactors(input);
  const nw = input.startGroup.width * sx;
  const nh = input.startGroup.height * sy;
  return {
    x: centeredX ? pivotX - nw / 2 : pivotX - (sign.x < 0 ? nw : 0),
    y: centeredY ? pivotY - nh / 2 : pivotY - (sign.y < 0 ? nh : 0),
    width: nw,
    height: nh,
  };
}

export interface GroupRotateInput {
  groupCenter: Point;
  members: readonly GroupMember[];
  startPointer: Point;
  pointer: Point;
  snap?: boolean;
  snapStep?: number;
  /** Snap without the explicit `snap` flag when the delta sits within this many degrees of a step multiple. */
  snapToleranceDeg?: number;
}

/** Angular delta (deg) for a group rotate gesture, snapped to `snapStep` multiples when `snap`. */
export function groupRotateDelta(input: GroupRotateInput): number {
  const { groupCenter: gc, startPointer, pointer } = input;
  const a0 = Math.atan2(startPointer.y - gc.y, startPointer.x - gc.x);
  const a1 = Math.atan2(pointer.y - gc.y, pointer.x - gc.x);
  const delta = ((a1 - a0) * 180) / Math.PI;
  if (input.snap || input.snapToleranceDeg !== undefined) {
    const step = input.snapStep ?? GROUP_ROTATE_SNAP_STEP;
    const snapped = Math.round(delta / step) * step;
    if (input.snap || Math.abs(delta - snapped) <= (input.snapToleranceDeg ?? 0)) return snapped;
  }

  return delta;
}

/**
 * Rotates each member's center about the group center by the gesture delta and adds the delta to its
 * own rotation. Member size is unchanged. Shift snaps the delta to 45°.
 */
export function groupRotate(input: GroupRotateInput): GroupMemberUpdate[] {
  const delta = groupRotateDelta(input);
  return input.members.map(m => {
    // Orbit the VISUAL centre about the group centre and translate the rect by that delta; rect.rotation += delta,
    // which the renderer composes about the same visual centre. Rotation is unchanged; only the pivot differs from
    // the plain rect centre for quarter-turned text.
    const p = m.pivot ?? center(m.rect);
    const np = rotateAround(p, input.groupCenter, delta);
    const rotation = (((m.rotation + delta) % 360) + 360) % 360;
    return {
      id: m.id,
      rect: { x: m.rect.x + (np.x - p.x), y: m.rect.y + (np.y - p.y), width: m.rect.width, height: m.rect.height },
      rotation,
    };
  });
}

// --- Move-alignment snap guides + rotation guide ---

export type SnapStopClass = 'page-edge' | 'page-center' | 'element';

/**
 * Capture/release radii per stop class in SCREEN px (zoom-invariant; converted to book px when stops are
 * built). The capture radius bounds the visible hop onto a stop, so every class stays single-digit —
 * industry range is 5-10 (moveable 5, tldraw/excalidraw 8, penpot 10); page anchors stay the strongest
 * class but only slightly, per владелец: элемент должен «допрыгивать пару пикселей», не телепортироваться.
 */
export const SNAP_CLASS_RADII_PX: Record<SnapStopClass, { capture: number; release: number }> = {
  'page-edge': { capture: 8, release: 10 },
  'page-center': { capture: 8, release: 8 },
  element: { capture: 5, release: 7 },
};

/** Equal-gap snap radii (screen px), same measurement set: holds within 4, releases at 6. */
export const SNAP_GAP_RADII_PX = { capture: 4, release: 6 };

/** How much closer (screen px) the rival snap kind must pull before it takes an axis from the incumbent.
 *  Pure tie-breaking hysteresis: without it two near-equal pulls swap the axis frame to frame and the
 *  guide flickers between an alignment line and a distance ruler. */
export const SNAP_ARBITRATION_MARGIN_PX = 0.5;

export interface SnapStop {
  pos: number;
  cls: SnapStopClass;
  /** Radii in book px (screen-px class radii × 1/viewScale at gesture start). */
  capture: number;
  release: number;
  /** Source rect's extent on the CROSS axis (book px) — lets the guide line span to its anchor object. */
  crossFrom: number;
  crossTo: number;
  /** Source object id; `''` for a page stop, which is always in play and never expires. */
  src: string;
  /** Distance from the dragged box to this stop's source (book px); `Infinity` for a page stop, which loses to any element. */
  srcGap: number;
}

export type SnapNeighborKind = 'frame' | 'text';

/**
 * Which classes a dragged box may align to, best first. An image frame lines up on image frames ONLY — a
 * caption sitting beside it is scenery, never the rail a photo grid is built on. Text reaches for frames
 * first and falls back to other text. Decor is absent from both roles: it neither anchors nor magnets.
 */
const SNAP_KIND_PRIORITY: Record<SnapNeighborKind, readonly SnapNeighborKind[]> = {
  frame: ['frame'],
  text: ['frame', 'text'],
};

/** Anchor priority of `kind` for a dragged `dragged` box — lower wins; `undefined` = not an anchor at all. */
export function snapKindRank(dragged: SnapNeighborKind, kind: SnapNeighborKind): number | undefined {
  const rank = SNAP_KIND_PRIORITY[dragged].indexOf(kind);
  return rank < 0 ? undefined : rank;
}

/** A snap-anchor candidate with the identity arbitration needs: which object it is and which class slot it fills. */
export interface SnapNeighbor extends TransformRect {
  id: string;
  kind: SnapNeighborKind;
}

export interface SnapStops {
  vertical: SnapStop[];
  horizontal: SnapStop[];
}

export type SnapAxis = 'V' | 'H';

export interface SnapGuide {
  axis: SnapAxis;
  pos: number;
  /** Cross-axis span (book px): union of the dragged box and the anchor object, so the line reads as a
   * relation between the two instead of a full-viewport cut «в никуда». */
  from: number;
  to: number;
}

/** Which stop an axis is currently held by (sticky hysteresis), and through which AABB edge it latched. */
export interface AxisSnapHold {
  stop: SnapStop;
  edgeFrac: 0 | 0.5 | 1;
}

export interface MoveSnapHold {
  v?: AxisSnapHold;
  h?: AxisSnapHold;
}

export interface MoveSnapResult {
  dx: number;
  dy: number;
  guides: SnapGuide[];
}

/**
 * Snap stops (book px): each rect's start/centre/end per axis. A page rect's edges and centre carry their
 * stronger page classes; any other rect is an element stop. `radiusScale` = 1/viewScale (screen→book).
 * `radiiOverride` (book px) pins every stop to one uniform radius — the legacy 2-screen-px behaviour for
 * hosts that supply no page geometry.
 */
export function collectSnapStops(
  rects: readonly TransformRect[],
  kind: 'page' | 'element',
  radiusScale: number,
  radiiOverride?: { capture: number; release: number },
): SnapStops {
  const vertical: SnapStop[] = [];
  const horizontal: SnapStop[] = [];
  const stop = (pos: number, cls: SnapStopClass, crossFrom: number, crossTo: number): SnapStop => {
    const r = radiiOverride ?? {
      capture: SNAP_CLASS_RADII_PX[cls].capture * radiusScale,
      release: SNAP_CLASS_RADII_PX[cls].release * radiusScale,
    };
    return { pos, cls, capture: r.capture, release: r.release, crossFrom, crossTo, src: '', srcGap: Infinity };
  };

  const edgeCls: SnapStopClass = kind === 'page' ? 'page-edge' : 'element';
  const centerCls: SnapStopClass = kind === 'page' ? 'page-center' : 'element';
  for (const r of rects) {
    const [y0, y1] = [r.y, r.y + r.height];
    const [x0, x1] = [r.x, r.x + r.width];
    vertical.push(stop(r.x, edgeCls, y0, y1), stop(r.x + r.width / 2, centerCls, y0, y1), stop(r.x + r.width, edgeCls, y0, y1));
    horizontal.push(
      stop(r.y, edgeCls, x0, x1),
      stop(r.y + r.height / 2, centerCls, x0, x1),
      stop(r.y + r.height, edgeCls, x0, x1),
    );
  }

  return { vertical, horizontal };
}

export function mergeSnapStops(a: SnapStops, b: SnapStops): SnapStops {
  return { vertical: [...a.vertical, ...b.vertical], horizontal: [...a.horizontal, ...b.horizontal] };
}

/**
 * Point stops for profile-declared page lines (trim edges, fraction guides): page-class anchors spanning
 * the full page, so they arbitrate like the page geometry they are derived from. Positions are book px;
 * `vertical` entries are x-coordinates, `horizontal` entries are y-coordinates.
 */
export function collectLineStops(
  positions: { vertical: readonly number[]; horizontal: readonly number[] },
  page: { width: number; height: number },
  radiusScale: number,
  radiiOverride?: { capture: number; release: number },
): SnapStops {
  const r = radiiOverride ?? {
    capture: SNAP_CLASS_RADII_PX['page-edge'].capture * radiusScale,
    release: SNAP_CLASS_RADII_PX['page-edge'].release * radiusScale,
  };
  const stop = (pos: number, crossTo: number): SnapStop => ({
    pos,
    cls: 'page-edge',
    capture: r.capture,
    release: r.release,
    crossFrom: 0,
    crossTo,
    src: '',
    srcGap: Infinity,
  });
  return {
    vertical: positions.vertical.map(p => stop(p, page.height)),
    horizontal: positions.horizontal.map(p => stop(p, page.width)),
  };
}

/** Gap between two 1-D spans (book px); 0 when they overlap. */
function spanGap(a0: number, a1: number, b0: number, b1: number): number {
  return b0 > a1 ? b0 - a1 : a0 > b1 ? a0 - b1 : 0;
}

/** Distance between two rects (book px): 0 while they overlap on both axes, else the gap across the plane. */
function rectGap(a: TransformRect, b: TransformRect): number {
  return Math.hypot(spanGap(a.x, a.x + a.width, b.x, b.x + b.width), spanGap(a.y, a.y + a.height, b.y, b.y + b.height));
}

/**
 * The neighbourhood one axis may align to: ONE element per side of the dragged box along that axis PER CLASS — its
 * direct frame neighbour and, for text, its direct text neighbour as well. Keeping only the best class per side
 * hid the caption beside a caption behind the photo above it, and a text lines up on the text next to it just as
 * often as on a frame. Class still decides the winner, but later: a stop only competes once the pointer is inside
 * its own capture radius, so the frame keeps every coordinate it actually offers. Sides are taken along the axis
 * being aligned, because that is where a row's left and right neighbours differ: splitting on the cross axis
 * instead collapses a whole row into one slot and drops the box's true neighbour. Density-independent on purpose,
 * not a radius in px.
 */
function nearestNeighbors(
  dragged: TransformRect,
  neighbors: readonly SnapNeighbor[],
  axis: SnapAxis,
  draggedKind: SnapNeighborKind,
): { rect: SnapNeighbor; gap: number }[] {
  const along = (r: TransformRect): [number, number] => (axis === 'V' ? [r.x, r.x + r.width] : [r.y, r.y + r.height]);
  const [d0, d1] = along(dragged);
  const dCenter = (d0 + d1) / 2;
  const bySlot = new Map<string, { rect: SnapNeighbor; gap: number; rank: number }>();
  for (const n of neighbors) {
    const rank = snapKindRank(draggedKind, n.kind);
    if (rank === undefined) continue;
    const [n0, n1] = along(n);
    const gap = rectGap(dragged, n);
    const slot = `${(n0 + n1) / 2 < dCenter ? '-' : '+'}${rank}`;
    const held = bySlot.get(slot);
    if (!held || gap < held.gap) bySlot.set(slot, { rect: n, gap, rank });
  }

  return [...bySlot.values()];
}

/**
 * Element stops of the dragged box's CURRENT neighbourhood — rebuilt per frame, so the magnet follows the box
 * instead of the layout it started in. Every stop carries its source id and cross-axis gap: that is what lets a
 * near element beat a far one offering the same coordinate.
 */
export function selectNeighborStops(
  dragged: TransformRect,
  neighbors: readonly SnapNeighbor[],
  radiusScale: number,
  draggedKind: SnapNeighborKind,
): SnapStops {
  const radii = SNAP_CLASS_RADII_PX.element;
  const stop = (pos: number, crossFrom: number, crossTo: number, src: string, srcGap: number): SnapStop => ({
    pos,
    cls: 'element',
    capture: radii.capture * radiusScale,
    release: radii.release * radiusScale,
    crossFrom,
    crossTo,
    src,
    srcGap,
  });

  const vertical: SnapStop[] = [];
  for (const { rect: r, gap } of nearestNeighbors(dragged, neighbors, 'V', draggedKind)) {
    const [y0, y1] = [r.y, r.y + r.height];
    vertical.push(
      stop(r.x, y0, y1, r.id, gap),
      stop(r.x + r.width / 2, y0, y1, r.id, gap),
      stop(r.x + r.width, y0, y1, r.id, gap),
    );
  }

  const horizontal: SnapStop[] = [];
  for (const { rect: r, gap } of nearestNeighbors(dragged, neighbors, 'H', draggedKind)) {
    const [x0, x1] = [r.x, r.x + r.width];
    horizontal.push(
      stop(r.y, x0, x1, r.id, gap),
      stop(r.y + r.height / 2, x0, x1, r.id, gap),
      stop(r.y + r.height, x0, x1, r.id, gap),
    );
  }

  return { vertical, horizontal };
}

// Arbitration in TWO keys: among stops inside their OWN capture radius, the NEAREST element (smallest distance from
// the dragged box) owns the axis, and only within one source does the closest coordinate win. Page anchors
// reach farther through bigger radii but sit at gap Infinity, so they never outrank an element genuinely beside
// the box — a far element offering the same coordinate can no longer steal the axis from the near neighbour.
function nearestAxisSnap(
  start: number,
  size: number,
  stops: readonly SnapStop[],
): { delta: number; pos: number; hold: AxisSnapHold } | undefined {
  const edges: readonly [number, 0 | 0.5 | 1][] = [
    [start, 0],
    [start + size / 2, 0.5],
    [start + size, 1],
  ];
  let best: { delta: number; pos: number; hold: AxisSnapHold } | undefined;
  let bestDiff = Infinity;
  let bestGap = Infinity;
  for (const stop of stops) {
    for (const [edge, edgeFrac] of edges) {
      const diff = Math.abs(stop.pos - edge);
      if (diff > stop.capture) continue;
      if (stop.srcGap > bestGap || (stop.srcGap === bestGap && diff >= bestDiff)) continue;
      bestGap = stop.srcGap;
      bestDiff = diff;
      best = { delta: stop.pos - edge, pos: stop.pos, hold: { stop, edgeFrac } };
    }
  }

  return best;
}

// A hold survives only while its anchor is still in the box's neighbourhood — otherwise dragging away along the
// captured coordinate keeps a guide running back to an element the box left several groups ago.
function stopSourceLive(stop: SnapStop, stops: readonly SnapStop[]): boolean {
  return stop.src === '' || stops.some(s => s.src === stop.src);
}

// Sticky hysteresis per axis: a held stop keeps the axis until the RAW position leaves its hold radius
// (max(capture, release)); only then does fresh capture run. Holding beats a nearer newcomer of the SAME
// standing deliberately — that is the "держится, отпускает после заметного увода" feel, and it is what
// makes page edges hold longest. The one thing stickiness may not do is keep a page anchor while a real
// element comes into range: crossing a page centre on the way must not cost the neighbour's own stop.
function resolveAxisSnap(
  start: number,
  size: number,
  stops: readonly SnapStop[],
  held: AxisSnapHold | undefined,
): { delta: number; pos?: number; held?: AxisSnapHold } {
  const fresh = nearestAxisSnap(start, size, stops);
  if (held && stopSourceLive(held.stop, stops) && !(fresh && fresh.hold.stop.srcGap < held.stop.srcGap)) {
    const edge = start + size * held.edgeFrac;
    const diff = Math.abs(held.stop.pos - edge);
    if (diff <= Math.max(held.stop.capture, held.stop.release)) {
      return { delta: held.stop.pos - edge, pos: held.stop.pos, held };
    }
  }

  if (fresh) return { delta: fresh.delta, pos: fresh.pos, held: fresh.hold };
  return { delta: 0 };
}

// Snap delta (book px) per axis (independent) toward its stop, plus the guide lines to render — at most
// one guide per axis (the winner's). `hold` carries the sticky state between frames of one gesture.
export function computeMoveSnap(
  draggedAABB: TransformRect,
  stops: SnapStops,
  hold: MoveSnapHold = {},
): MoveSnapResult & { hold: MoveSnapHold } {
  const v = resolveAxisSnap(draggedAABB.x, draggedAABB.width, stops.vertical, hold.v);
  const h = resolveAxisSnap(draggedAABB.y, draggedAABB.height, stops.horizontal, hold.h);
  const guides: SnapGuide[] = [];
  if (v.pos !== undefined && v.held)
    guides.push(anchoredGuide('V', v.pos, v.held.stop, draggedAABB.y, draggedAABB.y + draggedAABB.height));
  if (h.pos !== undefined && h.held)
    guides.push(anchoredGuide('H', h.pos, h.held.stop, draggedAABB.x, draggedAABB.x + draggedAABB.width));

  return { dx: v.delta, dy: h.delta, guides, hold: { v: v.held, h: h.held } };
}

// Guide spanning from the anchor object through the dragged box — never a page-wide cut.
function anchoredGuide(axis: SnapAxis, pos: number, stop: SnapStop, dragFrom: number, dragTo: number): SnapGuide {
  return { axis, pos, from: Math.min(stop.crossFrom, dragFrom), to: Math.max(stop.crossTo, dragTo) };
}

/** Which magnet owns a resize axis: an edge stop (page/element coordinate), or a neighbour's own dimension. */
export type ResizeSnapKind = 'edge' | 'size';

/**
 * Sticky per-axis state of the resize magnet, mirroring the move-snap hysteresis. Carries everything the
 * next frame needs to re-measure and re-draw the incumbent, because the candidates are rebuilt each frame.
 */
export interface ResizeAxisHold {
  kind: ResizeSnapKind;
  /** Edge: the stop coordinate. Size: the matched dimension (book px). */
  target: number;
  release: number;
  stop?: SnapStop;
  neighbor?: TransformRect;
}

export interface ResizeSnapHold {
  v?: ResizeAxisHold;
  h?: ResizeAxisHold;
}

/** A tick-ended dimension ruler (book px) — the equal-gap guide renderer draws these too. */
export interface RulerSegment {
  cross: number;
  from: number;
  to: number;
}

/** The paired ruler of a size match: one segment across the dragged box, one across the matched neighbour. */
export interface SizeGuide {
  axis: SnapAxis;
  segments: readonly RulerSegment[];
}

export interface ResizeSnapInput {
  anchor: ResizeAnchor;
  rect: TransformRect;
  stops: SnapStops;
  /** Neighbours whose own width/height the magnet may match. Omit → edge stops only (legacy hosts, Shift). */
  neighbors?: readonly SnapNeighbor[];
  /** Class of the box being resized — decides which neighbour classes may lend it a dimension. */
  draggedKind?: SnapNeighborKind;
  /** 1/viewScale: the element class radii are screen px, the sizes they bound are book px. */
  radiusScale?: number;
  hold?: ResizeSnapHold;
}

interface ResizeCandidate {
  kind: ResizeSnapKind;
  /** Size this candidate sets on the axis (book px), before the MIN_SIZE clamp. */
  size: number;
  diff: number;
  target: number;
  release: number;
  stop?: SnapStop;
  neighbor?: TransformRect;
}

// The rects whose dimensions the box may lock onto: its direct neighbour on each side of BOTH axes, so a row
// neighbour and a stacked one are equally reachable — a size match reads across the layout, not only along the
// axis being dragged. Density-independent, never a radius in px.
function sizeNeighborhood(
  rect: TransformRect,
  neighbors: readonly SnapNeighbor[],
  draggedKind: SnapNeighborKind,
): SnapNeighbor[] {
  const bySrc = new Map<string, SnapNeighbor>();
  for (const axis of ['V', 'H'] as const) {
    for (const { rect: r } of nearestNeighbors(rect, neighbors, axis, draggedKind)) bySrc.set(r.id, r);
  }

  return [...bySrc.values()];
}

/** How exactly the runner-up magnet must be honoured by the settled rect before it draws alongside the winner
 *  (screen px; the caller scales it by radiusScale into book px). A coincidence, not a second capture radius —
 *  the axis still moves by ONE delta. */
const DUAL_GUIDE_EPS = 0.5;

// One axis of the resize magnet. Both kinds compete on raw distance, and the incumbent is defended: it keeps
// the axis while its own target stays inside its release radius, and only a strictly closer challenger takes
// it over (a tie leaves the holder). Without that the two kinds swap frame to frame near a coincidence and
// the ruler flickers against the alignment line. The loser comes back as `alt`: it does not move the box, but
// when the settled rect satisfies it too the caller draws it — two coincidences, two confirmations.
function resolveResizeAxis(
  moving: number,
  fixed: number,
  dir: 1 | -1,
  size: number,
  stops: readonly SnapStop[],
  sizes: readonly { size: number; rect: SnapNeighbor }[],
  sizeRadii: { capture: number; release: number },
  held: ResizeAxisHold | undefined,
): { win: ResizeCandidate; alt?: ResizeCandidate } | undefined {
  const fresh: ResizeCandidate[] = [];

  let bestStop: SnapStop | undefined;
  let bestStopDiff = Infinity;
  let bestStopGap = Infinity;
  for (const s of stops) {
    const d = Math.abs(s.pos - moving);
    if (d > s.capture) continue;
    // Same two-key arbitration as the move path (nearestAxisSnap): the direct neighbour owns the axis and a
    // distant element offering the same coordinate cannot steal it; page stops (srcGap Infinity) yield to any element.
    if (s.srcGap > bestStopGap || (s.srcGap === bestStopGap && d >= bestStopDiff)) continue;
    bestStopGap = s.srcGap;
    bestStopDiff = d;
    bestStop = s;
  }

  if (bestStop) {
    fresh.push({
      kind: 'edge',
      size: (bestStop.pos - fixed) * dir,
      diff: bestStopDiff,
      target: bestStop.pos,
      release: bestStop.release,
      stop: bestStop,
    });
  }

  let bestSize: { size: number; rect: SnapNeighbor } | undefined;
  let bestSizeDiff = Infinity;
  for (const c of sizes) {
    const d = Math.abs(c.size - size);
    if (d <= sizeRadii.capture && d < bestSizeDiff) {
      bestSizeDiff = d;
      bestSize = c;
    }
  }

  if (bestSize) {
    fresh.push({
      kind: 'size',
      size: bestSize.size,
      diff: bestSizeDiff,
      target: bestSize.size,
      release: sizeRadii.release,
      neighbor: bestSize.rect,
    });
  }

  let winner: ResizeCandidate | undefined;
  if (held) {
    const diff = Math.abs(held.target - (held.kind === 'edge' ? moving : size));
    if (diff <= held.release) {
      if (held.kind === 'edge' && held.stop) {
        winner = { ...held, kind: 'edge', size: (held.target - fixed) * dir, diff };
      } else if (held.kind === 'size' && held.neighbor) {
        winner = { ...held, kind: 'size', size: held.target, diff };
      }
    }
  }

  for (const c of fresh) {
    if (!winner || c.diff < winner.diff) winner = c;
  }

  if (!winner) return undefined;
  const win = winner;
  return { win, alt: fresh.find(c => c.kind !== win.kind) };
}

// Does the runner-up magnet hold on the SETTLED rect? Then its guide draws next to the winner's.
function altSatisfied(alt: ResizeCandidate | undefined, moving: number, size: number, eps: number): boolean {
  if (!alt) return false;
  const off = alt.kind === 'edge' ? (alt.stop ? alt.stop.pos - moving : Infinity) : alt.target - size;
  return Math.abs(off) <= eps;
}

// The ruler pair for a matched dimension: one segment down the middle of the dragged box, one down the
// middle of the neighbour whose size it caught — the equality itself is the signal, so no numeric label.
function sizeGuide(axis: SnapAxis, dragged: TransformRect, neighbor: TransformRect): SizeGuide {
  const segment = (r: TransformRect): RulerSegment =>
    axis === 'V'
      ? { cross: r.y + r.height / 2, from: r.x, to: r.x + r.width }
      : { cross: r.x + r.width / 2, from: r.y, to: r.y + r.height };
  return { axis, segments: [segment(dragged), segment(neighbor)] };
}

// Resize-snap: pull the anchor's moving edge(s) onto a nearby stop OR onto a neighbour's own width/height
// (opposite edge fixed), only on axes the anchor controls; caller skips when the box is rotated or Alt is
// held. Returns the adjusted rect, the alignment guides, the size rulers, and the next frame's hold.
export function computeResizeSnap(input: ResizeSnapInput): {
  rect: TransformRect;
  guides: SnapGuide[];
  sizeGuides: SizeGuide[];
  hold: ResizeSnapHold;
} {
  const { anchor, rect, stops } = input;
  const sign = ANCHOR_SIGN[anchor];
  const neighbors = input.neighbors ?? [];
  const radiusScale = input.radiusScale ?? 1;
  const dualEps = DUAL_GUIDE_EPS * radiusScale;
  const sizeRadii = {
    capture: SNAP_CLASS_RADII_PX.element.capture * radiusScale,
    release: SNAP_CLASS_RADII_PX.element.release * radiusScale,
  };
  const pool = neighbors.length > 0 && input.draggedKind ? sizeNeighborhood(rect, neighbors, input.draggedKind) : [];
  let { x, y, width, height } = rect;
  const hold: ResizeSnapHold = {};

  let vWin: ResizeCandidate | undefined;
  let vAlt: ResizeCandidate | undefined;
  if (sign.x !== 0) {
    const right = x + width;
    const dir = sign.x === 1 ? 1 : -1;
    const res = resolveResizeAxis(
      dir === 1 ? right : x,
      dir === 1 ? x : right,
      dir,
      width,
      stops.vertical,
      pool.map(r => ({ size: r.width, rect: r })),
      sizeRadii,
      input.hold?.v,
    );
    vWin = res?.win;
    if (vWin) {
      width = Math.max(MIN_SIZE, vWin.size);
      if (dir === -1) x = right - width;
      hold.v = { kind: vWin.kind, target: vWin.target, release: vWin.release, stop: vWin.stop, neighbor: vWin.neighbor };
      if (altSatisfied(res?.alt, dir === 1 ? x + width : x, width, dualEps)) vAlt = res?.alt;
    }
  }

  let hWin: ResizeCandidate | undefined;
  let hAlt: ResizeCandidate | undefined;
  if (sign.y !== 0) {
    const bottom = y + height;
    const dir = sign.y === 1 ? 1 : -1;
    const res = resolveResizeAxis(
      dir === 1 ? bottom : y,
      dir === 1 ? y : bottom,
      dir,
      height,
      stops.horizontal,
      pool.map(r => ({ size: r.height, rect: r })),
      sizeRadii,
      input.hold?.h,
    );
    hWin = res?.win;
    if (hWin) {
      height = Math.max(MIN_SIZE, hWin.size);
      if (dir === -1) y = bottom - height;
      hold.h = { kind: hWin.kind, target: hWin.target, release: hWin.release, stop: hWin.stop, neighbor: hWin.neighbor };
      if (altSatisfied(res?.alt, dir === 1 ? y + height : y, height, dualEps)) hAlt = res?.alt;
    }
  }

  // Guides are built from the SETTLED rect, so a corner drag's width ruler spans the height it ended with.
  const snapped: TransformRect = { x, y, width, height };
  const guides: SnapGuide[] = [];
  const sizeGuides: SizeGuide[] = [];
  // Winner and (when the settled rect honours it too) runner-up both draw — the edge line and the width ruler
  // read as two independent confirmations of the same stop.
  for (const c of [vWin, vAlt]) {
    if (c?.stop && c.kind === 'edge') guides.push(anchoredGuide('V', c.stop.pos, c.stop, y, y + height));
    if (c?.neighbor && c.kind === 'size') sizeGuides.push(sizeGuide('V', snapped, c.neighbor));
  }

  for (const c of [hWin, hAlt]) {
    if (c?.stop && c.kind === 'edge') guides.push(anchoredGuide('H', c.stop.pos, c.stop, x, x + width));
    if (c?.neighbor && c.kind === 'size') sizeGuides.push(sizeGuide('H', snapped, c.neighbor));
  }

  return { rect: snapped, guides, sizeGuides, hold };
}

// Size-match resize snap: pull the moving edge so the box WIDTH/HEIGHT equals a sibling's, on axes the
// anchor controls and only where no edge-stop already snapped (edge alignment outranks size matching).
// The matched dimension reports the sibling's far edge as a guide so the eye finds what it matched.
export function computeSizeMatchSnap(
  anchor: ResizeAnchor,
  rect: TransformRect,
  neighbors: readonly TransformRect[],
  threshold: number,
  snappedAxes: ReadonlySet<SnapAxis>,
): { rect: TransformRect; guides: SnapGuide[] } {
  const sign = ANCHOR_SIGN[anchor];
  let { x, y, width, height } = rect;
  const guides: SnapGuide[] = [];
  const nearestSize = (target: number, sizes: readonly number[]): number | undefined => {
    let best: number | undefined;
    let bestDiff = threshold;
    for (const s of sizes) {
      const diff = Math.abs(s - target);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = s;
      }
    }

    return best;
  };

  if (sign.x !== 0 && !snappedAxes.has('V')) {
    const match = nearestSize(
      width,
      neighbors.map(n => n.width),
    );
    if (match !== undefined && match >= MIN_SIZE) {
      if (sign.x === 1) {
        width = match;
      } else {
        x = x + width - match;
        width = match;
      }

      guides.push({ axis: 'V', pos: sign.x === 1 ? x + width : x, from: y, to: y + height });
    }
  }

  if (sign.y !== 0 && !snappedAxes.has('H')) {
    const match = nearestSize(
      height,
      neighbors.map(n => n.height),
    );
    if (match !== undefined && match >= MIN_SIZE) {
      if (sign.y === 1) {
        height = match;
      } else {
        y = y + height - match;
        height = match;
      }

      guides.push({ axis: 'H', pos: sign.y === 1 ? y + height : y, from: x, to: x + width });
    }
  }

  return { rect: { x, y, width, height }, guides };
}

// The angle (multiple of 45°) when the whole-degree rotation lands exactly on one, else undefined.
export function rotationGuideAngle(deg: number, snaps: readonly number[] = ROTATION_SNAPS): number | undefined {
  const d = ((Math.round(deg) % 360) + 360) % 360;
  return snaps.includes(d) ? d : undefined;
}

export const CORNER_ANCHORS: readonly ResizeAnchor[] = ['top-left', 'top-right', 'bottom-right', 'bottom-left'];

/** A frame this thin has no room to give the edges: below it they grab only from OUTSIDE and the whole box moves. */
const MIN_MOVE_CORE_PX = 16;

/**
 * Inward the strips stay this shallow even on a roomy frame: the area just inside the frame line is CONTENT — on a
 * text layer it is the glyphs themselves, and a full-radius strip there turned a double-click into a resize grab
 * (the arm suppressed the trailing click, and a 3px hand tremor committed a micro-resize that silently dropped
 * auto-width). Outward reach is untouched — nothing competes with the strip outside the frame.
 */
const INNER_EDGE_REACH_PX = 4;

/**
 * How far a grab strip may reach INWARD along one axis. The outward reach is always the full radius — nothing
 * competes for it — but inward the four strips would meet on a small box and leave nothing to drag it by, which is
 * exactly how a caption-sized text layer became unmovable.
 */
export function inwardReach(extent: number, radius: number, minCore = MIN_MOVE_CORE_PX): number {
  return Math.max(0, Math.min(radius, (extent - minCore) / 2));
}

/**
 * Pointer in the frame's own axes: `u` along the top edge, `v` down the left one, with the frame's own extents.
 *
 * The edges need not be perpendicular: the offset is solved as `a·top + b·left` through the inverse of the edge
 * matrix, so the drawn corner of a sheared frame lands on `(w, h)` exactly. Projecting onto each edge alone leaks the
 * other edge's slant into the reading and would push that corner out of its own hit zone. `u = a·w` and `v = b·h` stay
 * pixel distances measured ALONG the edges. A frame whose edges collapse onto one line has no local axes; the
 * collapse is judged on `det / (w·h)`, the sine of the angle between the edges, so it does not depend on the frame size.
 */
function toFrameLocal(
  ptr: Point,
  anchors: Record<ResizeAnchor, Point>,
): { u: number; v: number; w: number; h: number } | undefined {
  const tl = anchors['top-left'];
  const top = { x: anchors['top-right'].x - tl.x, y: anchors['top-right'].y - tl.y };
  const left = { x: anchors['bottom-left'].x - tl.x, y: anchors['bottom-left'].y - tl.y };
  const w = Math.hypot(top.x, top.y);
  const h = Math.hypot(left.x, left.y);
  if (!(w > 0) || !(h > 0)) return undefined;
  const det = top.x * left.y - left.x * top.y;
  if (Math.abs(det) < 1e-9 * w * h) return undefined;
  const dx = ptr.x - tl.x;
  const dy = ptr.y - tl.y;
  const a = (left.y * dx - left.x * dy) / det;
  const b = (top.x * dy - top.y * dx) / det;
  return { u: a * w, v: b * h, w, h };
}

/**
 * Which transformer handle the pointer is over. Each side is grabbed along its WHOLE edge — there is no pill to aim
 * at — and a corner is simply where two of those strips cross, which is also what it does: both axes at once.
 * The middle of the frame belongs to nobody, so a plain drag there still moves the box.
 */
export function handleAtPoint(
  ptr: Point,
  anchors: Record<ResizeAnchor, Point>,
  rotate: Point,
  radius: number,
  minCore = MIN_MOVE_CORE_PX,
): HandleId | undefined {
  if (Math.hypot(ptr.x - rotate.x, ptr.y - rotate.y) <= radius) return 'rotate';
  const local = toFrameLocal(ptr, anchors);
  if (!local) return undefined;
  const { u, v, w, h } = local;
  if (u < -radius || u > w + radius || v < -radius || v > h + radius) return undefined;
  const inX = inwardReach(w, Math.min(radius, INNER_EDGE_REACH_PX), minCore);
  const inY = inwardReach(h, Math.min(radius, INNER_EDGE_REACH_PX), minCore);
  const left = u >= -radius && u <= inX;
  const right = w - u >= -radius && w - u <= inX;
  const top = v >= -radius && v <= inY;
  const bottom = h - v >= -radius && h - v <= inY;
  if (top && left) return 'top-left';
  if (top && right) return 'top-right';
  if (bottom && left) return 'bottom-left';
  if (bottom && right) return 'bottom-right';
  if (top) return 'top-center';
  if (bottom) return 'bottom-center';
  if (left) return 'middle-left';
  if (right) return 'middle-right';
  return undefined;
}

/**
 * Screen direction (degrees) a handle resizes along: the frame's two screen axes combined by the anchor's sign.
 *
 * Not the centre→handle vector — on a wide, short frame that vector points nearly sideways, so a corner would offer a
 * plain width cursor and hide that it moves BOTH axes at once. The sign of the anchor is what the drag actually does.
 * The axes are taken as UNIT vectors, so the box's aspect never leaks into the angle while its rotation and skew do.
 */
export function handleCursorAngle(anchor: ResizeAnchor, anchors: Record<ResizeAnchor, Point>): number {
  const unit = (from: Point, to: Point): Point => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.hypot(dx, dy) || 1;
    return { x: dx / len, y: dy / len };
  };

  const ax = unit(anchors['middle-left'], anchors['middle-right']);
  const ay = unit(anchors['top-center'], anchors['bottom-center']);
  const sign = ANCHOR_SIGN[anchor];
  const dir = { x: ax.x * sign.x + ay.x * sign.y, y: ax.y * sign.x + ay.y * sign.y };
  return (Math.atan2(dir.y, dir.x) * 180) / Math.PI;
}
